/* istanbul ignore file */
import React from 'react';
import { Provider } from 'react-redux';
import { CookieModal } from '@noths/polaris-client-gdpr-compliance';
import { GTM } from '@noths/polaris-client-google-analytics';
import { setLocaleCookies } from '@noths/polaris-client-localisation';
import type { BaseAppProps } from '@noths/polaris-client-next-components';
import { BaseApp } from '@noths/polaris-client-next-components';
import { SkipLinks, useSmartAppBanner } from '@noths/polaris-client-site-chrome-ui';
import { FavouritesContainer } from '@noths/polaris-client-user-favourites';
import type { Merge } from '@noths/polaris-dev-ts-recipes';
import type { AppContextPlusCookies } from '@noths/polaris-dev-ts-types';
import type { Store } from '@reduxjs/toolkit';
import { ConnectedRouter } from 'connected-next-router';
import type { AppProps } from 'next/app';
import NextApp from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';

import { PRODUCT_CONTAINER_WRAPPER, PRODUCTS_GRID_WRAPPER } from 'src/constants/elementIds';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { useStore } from 'src/redux/store';
import { fetchGlobalContent } from 'src/services/contentstack';
import type { NextConfig, PageProps } from 'src/types';
import { getFeatureFlagsWithMatchers } from 'src/utils/getFeatureFlagsWithMatchers';
import { isProd } from '../environment';
/*
  Enable mocks required for development for mock service worker.
  To enable mock service worker run:

  npx msw init ./public
*/
if (process.env.NODE_ENV === 'development') {
  // require('src/components/organisms/Filter/mocks/universal').worker.start();
  // require('src/components/organisms/FilterMenu/mocks/universal').worker.start();
  // require('src/components/organisms/Filter/mocks/withDelay/universal').worker.start();
  // require('src/components/organisms/ProductPage/mocks/universal').worker.start();
}

const nextConfig = getConfig() as NextConfig;
const AppComponent = BaseApp(nextConfig);

type CustomAppProps = Merge<
  BaseAppProps & AppProps,
  {
    pageProps: PageProps;
  }
>;

const App = (props: CustomAppProps) => {
  const { updatedReduxState, topBannerData } = props.pageProps || {};
  const store = useStore(props, updatedReduxState as ReduxApplicationState);
  const products = updatedReduxState?.products;
  const searchedTerm = products?.searchTerm || '';

  // Preload first row of product images to improve LCP
  // Note: for LCP metric, it may be sufficient to preload first product image only - further testing needed
  // As this code runs on server, we can't be sure whether to preload 2 or 3 images, so 3 preloaded
  const productItems = products?.items || {};
  const productPageIndexes = Object.keys(productItems);
  const firstProductPage = Array.isArray(productPageIndexes) && productItems[productPageIndexes[0]];
  const firstProductImage =
    firstProductPage?.length > 0 &&
    firstProductPage[0]?.images[0]?.href?.replace('{size}', 'preview');
  const secondProductImage =
    firstProductPage?.length > 1 &&
    firstProductPage[1]?.images[0]?.href?.replace('{size}', 'preview');
  const thirdProductImage =
    firstProductPage?.length > 2 &&
    firstProductPage[2]?.images[0]?.href?.replace('{size}', 'preview');

  useSmartAppBanner();

  return (
    <>
      <Head>
        {firstProductImage && <link rel="preload" as="image" href={firstProductImage} />}
        {secondProductImage && <link rel="preload" as="image" href={secondProductImage} />}
        {thirdProductImage && <link rel="preload" as="image" href={thirdProductImage} />}
        {process.env.NODE_ENV === 'production' && (
          <script
            src="https://tag.elevaate.technology/tag/retailer/notonthehighstreet.com/elevaate-tag.js"
            async
          />
        )}
        <CookieModal useTestScript={!isProd()} />
      </Head>
      <SkipLinks
        links={[
          { content: 'Skip to content', sectionId: PRODUCT_CONTAINER_WRAPPER },
          { content: 'Skip to products', sectionId: PRODUCTS_GRID_WRAPPER },
        ]}
      />
      <GTM />
      <Provider store={store as Store}>
        <ConnectedRouter>
          <FavouritesContainer nextConfig={nextConfig} />
          <AppComponent {...props} topBannerData={topBannerData} searchedTerm={searchedTerm} />
        </ConnectedRouter>
      </Provider>
    </>
  );
};

/*
  Necessary to disable next's Automatic Static Optimization feature.
  Without it this page will be statically compiled, and throw an error in
  `src/utils/gaPageEvent` due to undefined `pageProps.updatedReduxState`.

  Added in https://github.com/notonthehighstreet/polaris/pull/597.
  See below discussion for more details:
  https://github.com/notonthehighstreet/polaris/pull/597#discussion_r558444865
*/

App.getInitialProps = async (appContext: AppContextPlusCookies): Promise<BaseAppProps> => {
  const {
    router,
    ctx: { req, res },
  } = appContext;

  if (req && res) {
    setLocaleCookies({ req, res });
  }

  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};
  const appProps = await NextApp.getInitialProps(appContext);

  if (router.route === '/404') {
    const [navData] = await fetchGlobalContent({ featureFlags });

    return {
      ...appProps,
      navData,
      featureFlags,
    };
  }

  return {
    ...appProps,
    featureFlags,
  };
};

export default App;
